import React, { useEffect, useRef, useState } from "react";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { cn } from "@/lib/utils";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { formatExecutionCandleTypeLabel } from "../../../utils/executionCandleTimeframe";
import { formatStrategyRdRMultiple, strategyRdMatchKey } from "../../../utils/strategyRd";
import {
  strategyRdAllowedSidesNote,
  strategyRdMatchDisplayDirection,
} from "../../../utils/strategyRdPath";
import type { StrategyRdMatch, StrategyRdOhlcBar } from "../../../types/strategyRd";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import {
  StrategyRdMatchThumb,
  strategyRdMatchThumbCovers,
  type StrategyRdMatchThumbLevels,
} from "./StrategyRdMatchThumb";

/** A setup's backtest result, keyed `pattern_id:bar_time` (or `bar_time`). */
export type StrategyRdMatchOutcome = { r: number | null; result: string | null };

export type StrategyRdMatchRailProps = {
  matches: StrategyRdMatch[];
  selectedKey: string | null;
  timeZone: string;
  onSelect: (match: StrategyRdMatch) => void;
  /** Resolve match.meta.session_id → display label. */
  sessionLabelFor?: (match: StrategyRdMatch) => string | null;
  emptyMessage?: string | null;
  /** Backtest results, so each row shows how its trade ended. */
  outcomes?: Record<string, StrategyRdMatchOutcome> | null;
  /** The window's candles: each row shows its setup the way it looks on the chart. */
  candles?: readonly StrategyRdOhlcBar[] | null;
  candleType?: string;
  setupId?: string | null;
  confluences?: readonly (string | { id: string; candle_type?: string | null })[] | null;
  /** Entry / stop / target of each setup's trade, keyed like `selectedKey`. */
  levelsByMatchKey?: Record<string, StrategyRdMatchThumbLevels> | null;
};

/**
 * A row's mini chart, drawn only once the row scrolls into view (then kept): a backtest has
 * hundreds of setups, and drawing every thumbnail up front made the whole page slow.
 */
function LazyThumb({ children }: { children: () => React.ReactNode }): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(el);
    // Some hosts never deliver the observer's callback (a background or embedded view): check the
    // row's place by hand as well, on a timer and whenever the list scrolls.
    const inView = () => {
      const r = el.getBoundingClientRect();
      const list = el.closest("[data-strategy-rd-match-rail]")?.getBoundingClientRect();
      const top = list ? list.top - 200 : -200;
      const bottom = list ? list.bottom + 200 : window.innerHeight + 200;
      if (r.height > 0 && r.bottom >= top && r.top <= bottom) setShown(true);
    };
    const timer = window.setTimeout(inView, 350);
    const scroller = el.closest("[data-strategy-rd-match-rail]");
    scroller?.addEventListener("scroll", inView, { passive: true });
    return () => {
      io.disconnect();
      window.clearTimeout(timer);
      scroller?.removeEventListener("scroll", inView);
    };
  }, [shown]);
  return (
    <div ref={ref} className="h-24 w-full shrink-0 overflow-hidden rounded-[4px] border border-border bg-background">
      {shown ? children() : null}
    </div>
  );
}

function directionLabel(direction: StrategyRdMatch["direction"]): string {
  if (direction === "long") return "Long";
  if (direction === "short") return "Short";
  return "Neutral";
}

function outcomeLabel(o: StrategyRdMatchOutcome): string {
  const res = (o.result || "").toLowerCase();
  const word = res.includes("target") || res === "tp" ? "Target" : res.includes("stop") || res === "sl" ? "Stop" : res ? "Closed" : "";
  const r = formatStrategyRdRMultiple(o.r);
  return [word, r].filter(Boolean).join(" ");
}

/**
 * The setups in this window, newest-to-oldest as found: a compact list docked beside the chart
 * (it never covers it). Click a row — or use ↑ / ↓ — to jump the chart to that setup; the
 * selected row stays in view so you keep your place.
 */
export function StrategyRdMatchRail({
  matches,
  selectedKey,
  timeZone,
  onSelect,
  sessionLabelFor,
  emptyMessage = "No matches in this window. Try another pattern, timeframe, or lookback.",
  outcomes = null,
  candles = null,
  candleType,
  setupId = null,
  confluences = null,
  levelsByMatchKey = null,
}: StrategyRdMatchRailProps): JSX.Element {
  const showThumbs = Boolean(candles && candles.length > 0);
  const listRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!selectedKey || !listRef.current) return;
    const row = listRef.current.querySelector<HTMLElement>(`[data-strategy-rd-match-row="${CSS.escape(selectedKey)}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [selectedKey]);

  if (matches.length === 0) {
    return <p className={strategyRdMetaClassName}>{emptyMessage}</p>;
  }

  const step = (delta: number) => {
    const i = matches.findIndex((m) => strategyRdMatchKey(m) === selectedKey);
    const next = matches[Math.max(0, Math.min(matches.length - 1, (i < 0 ? 0 : i) + delta))];
    if (next) onSelect(next);
  };

  return (
    <ul
      ref={listRef}
      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain !p-1 !pr-2"
      data-strategy-rd-match-rail=""
      tabIndex={0}
      aria-label="Setups: use the arrow keys to step through them"
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          step(e.key === "ArrowDown" ? 1 : -1);
        }
      }}
    >
      {matches.map((match) => {
        const key = strategyRdMatchKey(match);
        const selected = key === selectedKey;
        const direction = strategyRdMatchDisplayDirection(match);
        const outcome = outcomes?.[`${match.pattern_id}:${match.bar_time}`] ?? outcomes?.[match.bar_time] ?? null;
        const outcomeColor =
          outcome?.r == null ? undefined : outcome.r > 0 ? PNL_POSITIVE_COLOR : outcome.r < 0 ? PNL_NEGATIVE_COLOR : undefined;
        const note = strategyRdAllowedSidesNote(match);
        const session = sessionLabelFor?.(match) ?? null;
        return (
          <li key={key}>
            {/* One card per setup, Trade Log style: side + time + outcome, where, then the chart. */}
            <button
              type="button"
              data-strategy-rd-match-row={key}
              aria-pressed={selected}
              tabIndex={-1}
              onClick={() => onSelect(match)}
              className={cn(
                "!flex w-full flex-col gap-2 !rounded-md !border !border-solid bg-card !p-2.5 text-left transition-colors",
                "font-[family-name:var(--font-primary)] dark:bg-sidebar",
                selected
                  ? // Inset: a ring outside the card is clipped by the scrolling list.
                  "!border-[color:var(--brand-tropical-surf)] shadow-[inset_0_0_0_1px_var(--brand-tropical-surf)]"
                  : "!border-border hover:!border-[color:var(--brand-tropical-surf)]/50"
              )}
            >
              <span className="flex w-full items-center gap-2">
                {direction === "long" || direction === "short" ? (
                  <TradePositionSideBadge side={direction} className="h-5 shrink-0 px-1.5 text-[11px]" />
                ) : (
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">{directionLabel(direction)}</span>
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-[color:var(--brand-tropical-surf)]">
                  {formatTradeDateTimeForDisplay(match.bar_time, timeZone)}
                </span>
                {outcome ? (
                  <span className="shrink-0 text-xs font-medium tabular-nums" style={{ color: outcomeColor }}>
                    {outcomeLabel(outcome)}
                  </span>
                ) : null}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {[session, formatExecutionCandleTypeLabel(match.candle_type) || match.candle_type].filter(Boolean).join(" · ")}
              </span>
              {showThumbs && strategyRdMatchThumbCovers(candles!, match) ? (
                <LazyThumb>
                  {() => (
                    <StrategyRdMatchThumb
                      match={match}
                      candles={candles!}
                      candleType={candleType || match.candle_type || "5m"}
                      setupId={setupId}
                      confluences={confluences}
                      levels={levelsByMatchKey?.[key] ?? null}
                    />
                  )}
                </LazyThumb>
              ) : null}
              {note ? <span className="text-xs leading-snug text-muted-foreground">{note}</span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
